import { z } from 'zod';
import { HSN_PATTERN, normalizeHsn } from '../tax.js';
import { ProductStatus, ProductType, WeightUnit, LengthUnit } from '../enums.js';
import { slugSchema, seoSchema } from './common.js';

const statusEnum = z.enum([ProductStatus.DRAFT, ProductStatus.ACTIVE, ProductStatus.ARCHIVED]);
const typeEnum = z.nativeEnum(ProductType);

/** Variant / SKU level fields (spec section 6). */
export const variantInputSchema = z.object({
  id: z.string().uuid().optional(),
  sku: z.string().min(1).max(64).trim(),
  barcode: z.string().max(64).trim().nullable().optional(),
  // e.g. { Size: "M", Color: "Sky", Finish: "Matte" }
  attributes: z.record(z.string().max(40), z.string().max(120)).default({}),
  pricePenceOverride: z.number().int().min(0).nullable().optional(),
  compareAtOverride: z.number().int().min(0).nullable().optional(),
  costPrice: z.number().int().min(0).nullable().optional(),
  weightGrams: z.number().min(0).max(200_000).nullable().optional(),
  lengthMm: z.number().min(0).max(3000).nullable().optional(),
  widthMm: z.number().min(0).max(3000).nullable().optional(),
  heightMm: z.number().min(0).max(3000).nullable().optional(),
  lowStockThreshold: z.number().int().min(0).nullable().optional(),
  /** Total on hand across locations. Applied through the stock ledger (needs inventory-adjust permission) when it differs from the current total. */
  stock: z.number().int().min(0).max(10_000_000).optional(),
  mediaIds: z.array(z.string().uuid()).max(20).default([]),
  isActive: z.boolean().default(true),
  position: z.number().int().min(0).default(0),
});

export const productPhysicalSchema = z.object({
  weightValue: z.number().min(0).nullable().optional(),
  weightUnit: z.enum([WeightUnit.G, WeightUnit.KG]).default(WeightUnit.G),
  lengthValue: z.number().min(0).nullable().optional(),
  widthValue: z.number().min(0).nullable().optional(),
  heightValue: z.number().min(0).nullable().optional(),
  dimensionUnit: z.enum([LengthUnit.MM, LengthUnit.CM]).default(LengthUnit.MM),
  packageWeightValue: z.number().min(0).nullable().optional(),
  packageLengthValue: z.number().min(0).nullable().optional(),
  packageWidthValue: z.number().min(0).nullable().optional(),
  packageHeightValue: z.number().min(0).nullable().optional(),
});

export const productContentSchema = z.object({
  shortDescription: z.string().max(400).default(''),
  description: z.string().max(20_000).default(''), // sanitized rich text
  materials: z.string().max(2000).default(''),
  includedItems: z.string().max(2000).default(''),
  usageInstructions: z.string().max(4000).default(''),
  careInstructions: z.string().max(2000).default(''),
  warnings: z.string().max(2000).default(''),
  ageGuidance: z.string().max(200).default(''),
});

export const productSafetySchema = z.object({
  ageRecommendation: z.string().max(80).nullable().optional(),
  supervisionNote: z.string().max(500).nullable().optional(),
  safetyDisclaimers: z.string().max(2000).nullable().optional(),
  requiresAgeGate: z.boolean().default(false),
});

export const customOptionDefinitionSchema = z.object({
  key: z.string().min(1).max(40),
  label: z.string().min(1).max(80),
  type: z.enum(['text', 'textarea', 'select', 'number', 'file']),
  required: z.boolean().default(false),
  options: z.array(z.string().max(80)).max(50).optional(),
  helpText: z.string().max(300).optional(),
});

export const productFulfillmentSchema = z.object({
  madeToOrder: z.boolean().default(false),
  leadTimeDays: z.number().int().min(0).max(365).nullable().optional(),
  returnEligible: z.boolean().default(true),
  returnWindowDays: z.number().int().min(0).max(365).default(7),
  fragile: z.boolean().default(false),
  shippingProfileId: z.string().uuid().nullable().optional(),
});

export const productInventoryDefaultsSchema = z.object({
  trackInventory: z.boolean().default(true),
  allowBackorder: z.boolean().default(false),
  lowStockThreshold: z.number().int().min(0).default(3),
  defaultLocationId: z.string().uuid().nullable().optional(),
});

export const productMediaRefSchema = z.object({
  mediaId: z.string().uuid(),
  role: z.enum(['primary', 'gallery', 'video']).default('gallery'),
  altText: z.string().max(300).default(''),
  position: z.number().int().min(0).default(0),
});

/** Full product create/update payload. Draft saves allow most fields to be empty. */
export const productInputSchema = z.object({
  // Identity
  name: z.string().min(1).max(200).trim(),
  slug: slugSchema.optional(),
  status: statusEnum.default(ProductStatus.DRAFT),
  productType: typeEnum.default(ProductType.STANDARD),
  brand: z.string().max(120).nullable().optional(),
  internalCode: z.string().max(60).nullable().optional(),

  // Catalog
  categoryId: z.string().uuid().nullable().optional(),
  secondaryCategoryIds: z.array(z.string().uuid()).max(20).default([]),
  collectionIds: z.array(z.string().uuid()).max(50).default([]),
  tags: z.array(z.string().max(40)).max(50).default([]),
  searchKeywords: z.string().max(1000).default(''),

  // Descriptions / content
  content: productContentSchema.partial().optional(),

  // Pricing
  basePrice: z.number().int().min(0).default(0),
  compareAtPrice: z.number().int().min(0).nullable().optional(),
  costPrice: z.number().int().min(0).nullable().optional(),
  currency: z.string().length(3).default('INR'),
  taxClass: z.string().max(40).default('standard'),
  taxable: z.boolean().default(true),
  /** HSN code for GST invoices: 4, 6 or 8 digits (spaces and dots are ignored); null clears it */
  hsnCode: z
    .string()
    .trim()
    .transform(normalizeHsn)
    .refine((v) => v === '' || HSN_PATTERN.test(v), 'An HSN code is 4, 6 or 8 digits')
    .transform((v) => (v === '' ? null : v))
    .nullable()
    .optional(),

  /** who sells it: a seller's id, or null for the store itself */
  sellerId: z.string().uuid().nullable().optional(),

  // Fulfillment
  fulfillment: productFulfillmentSchema.partial().optional(),

  // Physical
  physical: productPhysicalSchema.partial().optional(),

  // Inventory defaults
  inventoryDefaults: productInventoryDefaultsSchema.partial().optional(),

  // Media
  media: z.array(productMediaRefSchema).max(40).default([]),

  // SEO
  seo: seoSchema.optional(),

  // Custom options
  customizable: z.boolean().default(false),
  requiresCustomerNotes: z.boolean().default(false),
  allowsFileUpload: z.boolean().default(false),
  customOptions: z.array(customOptionDefinitionSchema).max(30).default([]),

  // Safety / compliance
  safety: productSafetySchema.partial().optional(),

  // Category-specific extension fields (flexible key/value, spec section 6)
  attributes: z.record(z.string().max(60), z.union([z.string(), z.number(), z.boolean()])).default({}),

  // Variants
  variants: z.array(variantInputSchema).min(1).max(200),

  // Merchandising
  relatedProductIds: z.array(z.string().uuid()).max(20).default([]),
  featured: z.boolean().default(false),
});

/** Looser schema for autosaved drafts (spec section 16). */
export const productDraftSchema = productInputSchema.partial().extend({
  name: z.string().min(1).max(200).trim(),
});

export type ProductInput = z.infer<typeof productInputSchema>;
export type VariantInput = z.infer<typeof variantInputSchema>;

/**
 * Publish/activation gate (spec section 16):
 * requires a slug, a category, >=1 active variant, a purchasable price and a
 * primary image. Shipping details (weight, size) are optional: a product with
 * no weight ships as 0g, i.e. the lowest shipping-rate band.
 */
export function validateForPublish(input: ProductInput): string[] {
  const errors: string[] = [];
  if (!input.slug) errors.push('Add a URL slug (the product’s web address) before publishing');
  if (!input.categoryId) errors.push('Choose a category before publishing');
  const activeVariants = input.variants.filter((v) => v.isActive);
  if (activeVariants.length === 0) errors.push('Add at least one active variant before publishing');
  if (input.basePrice <= 0 && !input.variants.some((v) => (v.pricePenceOverride ?? 0) > 0)) {
    errors.push('Set a price above ₹0 before publishing');
  }
  const hasPrimaryMedia = input.media.some((m) => m.role === 'primary');
  if (!hasPrimaryMedia) errors.push('Add a primary product image before publishing');

  if (input.safety?.requiresAgeGate && !input.safety?.ageRecommendation) {
    errors.push('Add an age recommendation: this product is age-gated');
  }
  return errors;
}
